// Full-document render of the Oscar thank-you (order confirmation) view:
// PageSkeleton (the React document shell) wrapping the legacy sealy layout --
// the "Rest easy" intro, the order-number/help content title, the shared
// RichOrderDetail block (the collapsible order-summary-details panel +
// grouped lines with named-status progress bars, typed-serialized
// server-side), the display-only OrderOffer islands, and the e-signature /
// support footer. The deferred GA4 dataLayer events (ga_ecom + ga_purchase)
// and Google Seller Ratings survey opt-in (ga_seller) inject after hydration.
// Header/footer slots are omitted so the default site chrome renders. The
// order-details toggle is owned by the shared block's own disclosure state
// (collapsible).
import React from "react";
import { RichOrderDetail } from "tsi-common-react/src/apps/customer-account/RichOrderDetail/index";
import { DataLayerPush } from "tsi-common-react/src/components/DataLayerPush/index";
import { GASeller } from "tsi-common-react/src/components/GASeller/index";

import { templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";
import { Template as OrderOffer } from "./OrderOfferTemplate";

export const Template = (
    props: templates.ThankYouDocumentTemplate,
): React.JSX.Element => {
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="thank-you-container l-capped-width u-margin-bottom--xl u-full-width-title">
                <div className="thank-you">
                    <div className="intro">
                        <h3 className="intro__title">
                            Rest easy, <span>{props.first_name}.</span>{" "}
                            We&apos;re working on your order.
                        </h3>
                        <p className="intro__copy">
                            You&apos;ll receive order confirmation emails at
                            each step of your order&apos;s progress, at{" "}
                            <strong>{props.email}</strong>. Check your order
                            status anytime at{" "}
                            <a
                                className="intro__link"
                                href={props.order_list_url}
                            >
                                Sealy.com
                            </a>
                            . Thanks for shopping with us.
                        </p>
                    </div>

                    <div className="content-title">
                        <h4 className="content-title__headline">
                            ORDER NUMBER: {props.order_number} |{" "}
                            {props.order_date}
                        </h4>
                        <span className="content-title__info">
                            Need Help?{" "}
                            <a href={props.cs_phone_rfc3966}>
                                {props.cs_phone_national}
                            </a>
                        </span>
                    </div>
                </div>

                {/* rich_order_detail is the sealy shape; the shared template
                    types it nullable (the other sites' shapes live in sibling
                    props), but on sealy it is always populated. */}
                {props.rich_order_detail && (
                    <RichOrderDetail
                        {...props.rich_order_detail}
                        collapsible={true}
                    />
                )}
                {props.offers.map((offer, i) => (
                    <div
                        key={i}
                        className="thank-you-order-offer l-capped-width"
                    >
                        <OrderOffer {...offer} />
                    </div>
                ))}
            </div>
            <div className="thank-you-esig">
                {props.has_esignature && (
                    <div className="thank-you-esig__copy">
                        <span className="thank-you-esig__copy--bold">
                            Electronic Signature:
                        </span>{" "}
                        Acknowledged &amp; Signed on {props.esig_signed_date}{" "}
                        {props.esig_signed_time}
                    </div>
                )}
                <div className="thank-you-esig__copy">
                    Questions or concerns? Talk to a Sleep Expert at{" "}
                    <a
                        className="thank-you-esig__copy--bold"
                        href={props.cs_phone_rfc3966}
                    >
                        {props.cs_phone_national}
                    </a>
                    .
                </div>
            </div>
            <DataLayerPush events={props.ga_events} />
            <GASeller surveyData={props.ga_seller_survey_data} />
        </PageSkeleton>
    );
};
